import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    MenuItem,
    Alert,
    LinearProgress,
    Breadcrumbs,
    Link,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Email,
    Send,
    CheckCircle,
    Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';

type EmailType = 'verification' | 'reset-password' | 'invitation';

const emailTypeLabels: Record<EmailType, string> = {
    'verification': 'Verifizierungs-E-Mail',
    'reset-password': 'Passwort-Reset-E-Mail',
    'invitation': 'Einladungs-E-Mail'
};

export const EmailTestManagement: FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [emailType, setEmailType] = useState<EmailType>('verification');
    const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);

    const handleSendTestEmail = async () => {
        if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            enqueueSnackbar('Bitte geben Sie eine gültige E-Mail-Adresse ein', { variant: 'error' });
            return;
        }

        try {
            setLoading(true);
            setLastResult(null);
            const result = await adminApiClient.testEmail(email, emailType);
            setLastResult(result);
            enqueueSnackbar('Test-E-Mail erfolgreich gesendet', { variant: 'success' });
        } catch (error: any) {
            console.error('Error sending test email:', error);
            setLastResult({
                success: false,
                message: error?.message || 'Fehler beim Senden der Test-E-Mail'
            });
            enqueueSnackbar('Fehler beim Senden der Test-E-Mail', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumb */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link 
                    color="inherit" 
                    href="/app/profile" 
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/app/profile');
                    }}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Profil
                </Link>
                <Link 
                    color="inherit" 
                    href="/app/admin" 
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/app/admin');
                    }}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Admin Dashboard
                </Link>
                <Typography color="text.primary">E-Mail Test</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1">
                            E-Mail Test
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Testen Sie den E-Mail-Versand mit verschiedenen E-Mail-Typen
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Info Card */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Was ist E-Mail Test?</strong><br />
                    Diese Funktion ermöglicht es, Test-E-Mails an eine angegebene E-Mail-Adresse zu senden. 
                    Sie können zwischen verschiedenen E-Mail-Typen wählen: Verifizierungs-E-Mail, Passwort-Reset-E-Mail oder Einladungs-E-Mail.
                </Typography>
            </Alert>

            {/* Test Form */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Test-E-Mail senden
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
                    <TextField
                        label="E-Mail-Adresse"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="test@example.com"
                        fullWidth
                        disabled={loading}
                        required
                    />

                    <FormControl fullWidth disabled={loading}>
                        <InputLabel>E-Mail-Typ</InputLabel>
                        <Select
                            value={emailType}
                            onChange={(e) => setEmailType(e.target.value as EmailType)}
                            label="E-Mail-Typ"
                        >
                            <MenuItem value="verification">
                                {emailTypeLabels['verification']}
                            </MenuItem>
                            <MenuItem value="reset-password">
                                {emailTypeLabels['reset-password']}
                            </MenuItem>
                            <MenuItem value="invitation">
                                {emailTypeLabels['invitation']}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        startIcon={<Send />}
                        onClick={handleSendTestEmail}
                        disabled={loading || !email}
                        color="primary"
                        size="large"
                    >
                        Test-E-Mail senden
                    </Button>
                </Box>
            </Paper>

            {/* Loading Indicator */}
            {loading && <LinearProgress sx={{ mb: 3 }} />}

            {/* Results */}
            {lastResult && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {lastResult.success ? (
                            <CheckCircle color="success" />
                        ) : (
                            <ErrorIcon color="error" />
                        )}
                        Ergebnis
                    </Typography>
                    
                    <Alert 
                        severity={lastResult.success ? 'success' : 'error'} 
                        sx={{ mt: 2 }}
                    >
                        <Typography variant="body1">
                            {lastResult.message}
                        </Typography>
                    </Alert>
                </Paper>
            )}
        </Box>
    );
};

